import type { ChatBucket, TimelineGap, Waveform } from "../../api/types";
import { formatDate, formatTimeSeconds } from "../../time/format";
import {
  type View,
  gridLines,
  gridStepMs,
  place,
  timeToX,
  viewEnd,
  viewStart,
  xToTime,
} from "../geometry";
import { type Bands, theme, waveAmplitude } from "../theme";

/**
 * The chat lane: one bar per bucket, scaled against the busiest bucket on screen.
 *
 * Relative rather than absolute scaling, because there is no meaningful absolute maximum --
 * a quiet Tuesday and a packed guest set differ by orders of magnitude, and a fixed ceiling
 * would render one of them as a flat line. The question the lane answers is "when was this
 * window busy", which is a comparison within the window.
 */
export function drawChatDensity(
  ctx: CanvasRenderingContext2D,
  view: View,
  buckets: readonly ChatBucket[],
  gaps: readonly TimelineGap[],
  b: Bands,
): void {
  const laneHeight = b.chatBottom - b.chatTop;
  if (laneHeight <= 0) return;

  // A baseline, so an empty lane still reads as a lane rather than as missing UI.
  ctx.fillStyle = theme.chatBaseline;
  ctx.fillRect(0, b.chatBottom - 1, view.width, 1);

  // Unlogged stretches first, so bars draw over them rather than being hidden.
  for (const gap of place(view, gaps, 1)) {
    const x0 = Math.max(0, gap.x0);
    const x1 = Math.min(view.width, gap.x1);
    if (x1 <= x0) continue;
    ctx.fillStyle = theme.chatUnlogged;
    ctx.fillRect(x0, b.chatTop, x1 - x0, laneHeight);
  }

  if (buckets.length === 0) return;

  let busiest = 0;
  for (const bucket of buckets) busiest = Math.max(busiest, bucket.count);
  if (busiest <= 0) return;

  for (const placed of place(view, buckets, 1)) {
    const x0 = Math.max(0, placed.x0);
    const x1 = Math.min(view.width, placed.x1);
    if (x1 <= x0) continue;

    // Square root, not linear: a linear scale against the busiest bucket flattens everything
    // else into invisibility the moment one minute of the window is unusually loud.
    const share = Math.sqrt(placed.item.count / busiest);
    // At least a pixel. A bucket with messages in it must never render as nothing -- that
    // would make "one person said something" indistinguishable from silence.
    const barHeight = Math.max(1, Math.round(share * (laneHeight - 2)));

    ctx.fillStyle = placed.item.count >= busiest / 4 ? theme.chatFill : theme.chatFillQuiet;
    ctx.fillRect(x0, b.chatBottom - barHeight, Math.max(1, x1 - x0), barHeight);
  }
}

/**
 * The loudness envelope, mirrored about the middle of the content band.
 *
 * Placed from the response's own span rather than the view's, so a window still in flight draws
 * the audio it actually describes and slides away, instead of stretching the last answer over a
 * range it never covered.
 */
export function drawWaveform(
  ctx: CanvasRenderingContext2D,
  view: View,
  wave: Waveform,
  b: Bands,
): void {
  const startMs = Date.parse(wave.start);
  const spanMs = Date.parse(wave.end) - startMs;
  if (!(spanMs > 0) || wave.peaks.length === 0) return;
  const columnMs = spanMs / wave.peaks.length;
  const width = Math.max(1, columnMs / view.msPerPx);
  const mid = (b.contentTop + b.contentBottom) / 2;
  const half = (b.contentBottom - b.contentTop) / 2 - 2;
  // The fetch window is three viewports wide; only the middle one can be seen.
  const first = Math.max(0, Math.floor((xToTime(view, 0) - startMs) / columnMs));
  const last = Math.min(
    wave.peaks.length,
    Math.ceil((xToTime(view, view.width) - startMs) / columnMs),
  );
  ctx.fillStyle = theme.waveform;
  for (let i = first; i < last; i++) {
    const amp = waveAmplitude(wave.peaks[i]!);
    if (amp <= 0) continue;
    const h = amp * half;
    ctx.fillRect(timeToX(view, startMs + i * columnMs), mid - h, width, h * 2);
  }
}

export function drawGrid(
  ctx: CanvasRenderingContext2D,
  view: View,
  height: number,
  b: Bands,
): void {
  const step = gridStepMs(view.msPerPx);
  ctx.lineWidth = 1;
  for (const t of gridLines(view, step)) {
    // Half-pixel offset, or a 1 px line straddles two device pixels and renders as a
    // 2 px blur at every gridline.
    const x = Math.round(timeToX(view, t)) + 0.5;
    ctx.strokeStyle = theme.gridline;
    ctx.beginPath();
    ctx.moveTo(x, b.rulerBottom);
    ctx.lineTo(x, height);
    ctx.stroke();
  }
}

/**
 * The three labels the spec asks for: window start, centre, and end.
 *
 * The date sits above the time and the time is centred against the date, which is what
 * produces the indented look in the specification:
 *
 *     27 May 2026
 *       10:13:47
 *
 * So the time is not drawn at the label's anchor -- it is drawn at the centre of the date
 * that was just measured.
 *
 * Seconds, not just minutes, and the centre one is why: it sits on the playhead, so at the live
 * edge it is the only clock on the page and a value that changed once a minute read as a clock
 * that had stopped. The three stay in the same format because they are one ruler -- reading the
 * span off a start and end of different precision is arithmetic nobody should be asked to do.
 * Seconds also fit: `formatTimeSeconds` at 15px is still narrower than the date above it, so
 * nothing here overflows the anchor it is measured against.
 */
export function drawRuler(ctx: CanvasRenderingContext2D, view: View, zone: string, b: Bands): void {
  const start = viewStart(view);
  const end = viewEnd(view);
  const mid = view.centerMs;

  ctx.textBaseline = "top";
  drawStackedLabel(ctx, start, zone, 10, b, "left");
  drawStackedLabel(ctx, mid, zone, view.width / 2, b, "center");
  drawStackedLabel(ctx, end, zone, view.width - 10, b, "right");

  ctx.strokeStyle = theme.gridlineMajor;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(0, b.rulerBottom - 0.5);
  ctx.lineTo(view.width, b.rulerBottom - 0.5);
  ctx.stroke();
}

function drawStackedLabel(
  ctx: CanvasRenderingContext2D,
  ms: number,
  zone: string,
  anchorX: number,
  b: Bands,
  align: "left" | "center" | "right",
): void {
  const date = formatDate(ms, zone);
  const time = formatTimeSeconds(ms, zone);

  ctx.font = "600 12px ui-sans-serif, system-ui, sans-serif";
  const dateWidth = ctx.measureText(date).width;

  let dateLeft: number;
  if (align === "left") dateLeft = anchorX;
  else if (align === "right") dateLeft = anchorX - dateWidth;
  else dateLeft = anchorX - dateWidth / 2;

  ctx.textAlign = "left";
  ctx.fillStyle = theme.labelDim;
  ctx.fillText(date, dateLeft, b.rulerTop + 6);

  // Centred against the date above it, not against the anchor.
  ctx.font = "600 15px ui-sans-serif, system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.fillStyle = theme.label;
  ctx.fillText(time, dateLeft + dateWidth / 2, b.rulerTop + 22);
  ctx.textAlign = "left";
}
